import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactGA from 'react-ga4'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Toolbar from '@mui/material/Toolbar'
import { NavigationBarDesktop, NavigationBarMobile } from '@xiam/xiam-ui-toolkit'
import {
  useNavigate,
  useLocation
} from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import XIAMRouters from './features/XIAMRouters/XIAMRouters'
import {
  selectOverlayError,
  setOverlayError,
  selectOAuthError
} from './features/error/errorSlice'
import './App.scss'
import {
  BANNER_MENU_SECTIONS,
  BANNER_DROPDOWN_MENU_SECTIONS,
  GA_ID,
  HAMBURGER_MENU_SECTIONS
} from './util/config'
import './util/i18n'
import isMobileView from './util/MobileView'
import { selectUserObject } from './features/user/userSlice'
import { selectProfilePictureImage } from './features/user/profilePictureSlice'
import { ErrorPage } from './features/error/ErrorPage'
import { isAuthenticated } from './util/auth'
import { fetchSurveyTemplates } from './pages/Survey/surveyTemplateSlice'
import { resetSupportForm } from './pages/Support/supportFormSlice'
// import logo from './assets/JNJOneAccess-logo-red.svg'
import styles from './scss/NavigationBar.module.scss'
import logo from './assets/jj_red_logo.svg'
import logoMobile from './assets/jj_red_logo_mobile.svg'
import '@xiam/xiam-ui-toolkit/dist/xiam-ui-toolkit.css'

if (GA_ID) {
  ReactGA.initialize(GA_ID)
}

const Content = React.memo(() => {
  const overlayError = useSelector(selectOverlayError)
  const { t, i18n } = useTranslation()
  const location = useLocation().pathname
  const prevPath = useRef(location)

  const navigate = useNavigate()
  const oauthError = useSelector(selectOAuthError)
  const dispatch = useDispatch()

  const hasError = oauthError || overlayError

  const [isMobile, setIsMobile] = useState(isMobileView())

  const userObjData = useSelector(selectUserObject)
  const userProfilePictureData = useSelector(selectProfilePictureImage)
  const userInfo = {
    profilePictureData: userProfilePictureData,
    firstName: userObjData.firstName,
    lastName: userObjData.lastName,
    lastLogin: userObjData.lastLogin,
  }

  useEffect(() => {
    const preferredLang = userObjData.preferredLanguage
    if (Array.isArray(i18n.resourceKeys) && i18n.resourceKeys.includes(preferredLang)) {
      i18n.changeLanguage(preferredLang)
    }
  }, [userObjData])

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(isMobileView())
    }

    window.addEventListener('resize', handleResize)

    handleResize()

    dispatch(fetchSurveyTemplates())

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    const wasOnSupportFormPage = prevPath.current === '/support'
    const isNowOnSupportFormPage = location === '/support'

    if (wasOnSupportFormPage && !isNowOnSupportFormPage) {
      dispatch(resetSupportForm())
    }
    prevPath.current = location
  }, [location, dispatch])

  // if (!authState) {
  //   return null
  // }

  // desktop view
  if (!isMobile) {
    return (
      <Box sx={{ display: 'flex' }}>
        <Grid container direction="row" alignItems="flex-start">
          <Grid item xs={12}>
            {isAuthenticated ? (
              <NavigationBarDesktop
                logo={logo}
                dropdownMenuLinks={BANNER_DROPDOWN_MENU_SECTIONS}
                errorBar={hasError}
                links={BANNER_MENU_SECTIONS}
                onSignIn={() => navigate('/logout')}
                showUserProfile={isAuthenticated}
                signInButtonText={t('Sign Out')}
                userInfo={userInfo}
                customStyle={styles}
                secondLogo="One Access"
              />
            ) : (
              <NavigationBarDesktop
                userInfo={userInfo}
                logo={logo}
                links={[]}
                dropdownMenuLinks={[]}
                showUserProfile={isAuthenticated}
                signInButtonText={t('sign-out-tab')}
                onSignIn={() => navigate('/login')}
                errorBar={hasError}
              />
            )}
            {overlayError.message && location !== '/health' ? (
              <ErrorPage />
            ) : (
              <Box
                sx={{
                  flexGrow: 1,
                  width: 'calc(100% - widthOfSideMenu)',
                  ml: 'widthOfSideMenu',
                }}
              >
                <XIAMRouters />
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
    )
  }

  // mobile view
  const shouldShowToolbar = isAuthenticated && location !== '/login'
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', overflowX: 'hidden' }}>
      {(isAuthenticated && !overlayError.message) ? (
        <NavigationBarMobile
          errorBar={hasError}
          links={BANNER_MENU_SECTIONS}
          hamburguerMenuLinks={HAMBURGER_MENU_SECTIONS}
          onSignIn={() => navigate('/logout')}
          signInButtonText={t('Sign Out')}
          logo={logoMobile}
          userInfo={userInfo}
          showUserProfile={isAuthenticated}
          customStyle={styles}
          secondLogo="One Access"
        />
      ) : (
        <NavigationBarMobile
          errorBar={hasError}
          links={BANNER_MENU_SECTIONS}
          hamburguerMenuLinks={HAMBURGER_MENU_SECTIONS}
          onSignIn={() => navigate('/login')}
          signInButtonText={t('sign-out-tab')}
          logo={logoMobile}
          userInfo={userInfo}
          showUserProfile={isAuthenticated}
          customStyle={styles}
          secondLogo="One Access"
        />
      )}

      {isAuthenticated && (
        <Box sx={{ display: 'flex' }}>
          <Box sx={{ flexGrow: 1 }}>
            {overlayError.message && location !== '/health' ? (
              <ErrorPage />
            ) : (
              (
                <>
                  {shouldShowToolbar && <Toolbar />}
                  <XIAMRouters />
                </>
              )
            )}
          </Box>
        </Box>
      )}
    </Box>
  )
})

function App() {
  const dispatch = useDispatch()
  const overlayError = useSelector(selectOverlayError)

  useEffect(() => {
    dispatch(
      setOverlayError({
        message: overlayError.message || '',
        code: overlayError.code || undefined,
      })
    )
  }, [overlayError, dispatch])

  const [isErrorAlertOpen, setIsErrorAlertOpen] = React.useState(false)

  const closeErrorAlert = () => {
    setIsErrorAlertOpen(false)
  }

  return (
    <div className="App">
      <Content
        isErrorAlertOpen={isErrorAlertOpen}
        closeErrorAlert={closeErrorAlert}
        overlayError={overlayError}
      />
    </div>
  )
}

Content.propTypes = {
  overlayError: PropTypes.shape({
    message: PropTypes.string
  }).isRequired
}

export default App
